import { useState } from 'react';
import { Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { SectionHead } from '@/components/layout';
import { Button, Container, Pill, Text } from '@/components/ui';
import { FaqAccordion } from '@/components/marketing';
import { SeoHead } from '@/components/SeoHead';
import { supabase } from '@/lib/supabase';
import { useHover } from '@/lib/useHover';
import { MoodSection, fluidFontSize, fluidLineHeight, palette, useTheme } from '@/theme';

const webTransition = (props = 'background-color, color, transform, border-color', dur = '180ms') =>
  Platform.OS === 'web' ? ({ transitionProperty: props, transitionDuration: dur } as any) : null;

type StudioEntry = {
  label: string;
  pill: string;
  name: string;
  nameItalic: string;
  role: string;
  address: string;
  city: string;
  body: string;
  initial: string;
};
type StudioDecorated = StudioEntry & { color: 'verde' | 'wine' };

const STUDIO_COLORS: Array<'verde' | 'wine'> = ['verde', 'wine'];

type SocialEntry = { name: string; initial: string; handle: string; body: string; cta: string };
type SocialDecorated = SocialEntry & { bg: string; dark?: boolean };

const SOCIAL_DECORATIONS: Record<string, { bg: string; dark?: boolean }> = {
  Instagram: { bg: palette.wine },
  Facebook: { bg: palette.verdeSlate },
  YouTube: { bg: palette.ember },
  WhatsApp: { bg: palette.gold, dark: true },
};

type FaqItem = { q: string; a: string };
type HoursRow = { day: string; text: string };

export default function KontaktScreen() {
  const theme = useTheme();
  const { t } = useTranslation();

  const topics = t('kontakt.form.topics', { returnObjects: true }) as string[];
  const studios = (t('kontakt.standorte.studios', { returnObjects: true }) as StudioEntry[]).map<
    StudioDecorated
  >((s, i) => ({ ...s, color: STUDIO_COLORS[i] ?? 'wine' }));
  const socials = (t('kontakt.social.items', { returnObjects: true }) as SocialEntry[]).map<
    SocialDecorated
  >((s) => ({ ...s, ...(SOCIAL_DECORATIONS[s.name] ?? { bg: palette.wine }) }));
  const faqItems = t('kontakt.faq.items', { returnObjects: true }) as FaqItem[];
  const hoursRows = t('kontakt.hours.rows', { returnObjects: true }) as HoursRow[];

  const [topic, setTopic] = useState<string>(topics[0]);
  const [form, setForm] = useState({ name: '', firma: '', tel: '', email: '', betreff: '', nachricht: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (k: keyof typeof form) => (v: string) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async () => {
    if (!form.email || !form.nachricht || !form.name) {
      setError(t('kontakt.form.errors.required'));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const { error: insertError } = await (supabase.from('contact_submissions') as any).insert([
        {
          name: form.name,
          firma: form.firma || null,
          tel: form.tel || null,
          email: form.email,
          topic,
          subject: form.betreff || null,
          message: form.nachricht,
        },
      ]);
      if (insertError) console.warn('contact_submissions insert failed:', insertError.message);
      setDone(true);
    } catch (e) {
      console.warn(e);
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View>
      <SeoHead
        title={t('kontakt.seo.title')}
        description={t('kontakt.seo.description')}
      />

      {/* Pagehead with pulse promise */}
      <View style={{ paddingTop: 48, paddingBottom: 64 }}>
        <Container>
          <View style={st.eyebrowRow}>
            <View style={[st.dot, { backgroundColor: theme.colors.accentAlt }]} />
            <Text variant="meta" tone="fgMute">{t('kontakt.hero.eyebrow')}</Text>
          </View>
          <Text
            style={{
              fontFamily: theme.fontFamily.display,
              fontSize: fluidFontSize(48, 11.5, 96) as any,
              lineHeight: fluidLineHeight(0.95, 92) as any,
              color: theme.colors.fg,
              letterSpacing: -2,
            }}
          >
            {t('kontakt.hero.titleLine1')}{'\n'}
            <Text italic style={{ fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic', color: theme.colors.accent, fontSize: fluidFontSize(48, 11.5, 96) as any }}>
              {t('kontakt.hero.titleLine2')}
            </Text>
          </Text>
          <Text variant="bodyLarge" tone="fgSoft" style={{ marginTop: 24, maxWidth: 680 }}>
            {t('kontakt.hero.intro')}
          </Text>
          <View
            style={[
              st.pulse,
              { backgroundColor: theme.colors.bgSoft, borderColor: theme.colors.rule, borderRadius: 999 },
            ]}
          >
            <View style={[st.pulseDot, { backgroundColor: theme.colors.accentAlt }]} />
            <Text style={{ color: theme.colors.fgSoft }}>
              <Text italic style={{ fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic', color: theme.colors.accent }}>
                {t('kontakt.hero.pulse.highlight')}
              </Text>{' '}
              {t('kontakt.hero.pulse.rest')}
            </Text>
          </View>
        </Container>
      </View>

      {/* 01 Form + side */}
      <View style={{ paddingVertical: 32 }}>
        <Container>
          <View style={st.formGrid}>
            <View
              style={[
                st.formCard,
                { backgroundColor: theme.colors.bgSoft, borderColor: theme.colors.rule, borderRadius: 12 },
              ]}
            >
              <Text variant="meta" tone="fgMute">{t('kontakt.form.eyebrow')}</Text>
              <Text
                style={{
                  fontFamily: theme.fontFamily.display,
                  fontSize: 40,
                  lineHeight: 42,
                  color: theme.colors.fg,
                  marginTop: 12,
                }}
              >
                {t('kontakt.form.titlePart1')}
                <Text italic style={{ fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic', color: theme.colors.accent, fontSize: 40 }}>
                  {t('kontakt.form.titleItalic')}
                </Text>
              </Text>
              <Text variant="body" tone="fgSoft" style={{ marginTop: 12 }}>
                {t('kontakt.form.intro')}
              </Text>

              <View style={st.chipRow}>
                {topics.map((tp) => (
                  <TopicChip key={tp} label={tp} active={topic === tp} onPress={() => setTopic(tp)} />
                ))}
              </View>

              <View style={st.fieldGrid}>
                <Field label={t('kontakt.form.fields.name')} value={form.name} onChangeText={update('name')} />
                <Field label={t('kontakt.form.fields.firma')} value={form.firma} onChangeText={update('firma')} />
                <Field label={t('kontakt.form.fields.tel')} value={form.tel} onChangeText={update('tel')} />
                <Field label={t('kontakt.form.fields.email')} value={form.email} onChangeText={update('email')} kind="email" />
                <Field label={t('kontakt.form.fields.betreff')} value={form.betreff} onChangeText={update('betreff')} full />
                <Field label={t('kontakt.form.fields.nachricht')} value={form.nachricht} onChangeText={update('nachricht')} multiline full />
              </View>

              {error ? <Text style={{ color: '#cc4466', marginTop: 12 }}>{error}</Text> : null}
              {done ? (
                <View style={[st.thanks, { backgroundColor: theme.colors.bg, borderRadius: 12 }]}>
                  <Text variant="h4">{t('kontakt.form.done.title')}</Text>
                  <Text variant="body" tone="fgSoft" style={{ marginTop: 8 }}>
                    {t('kontakt.form.done.body')}
                  </Text>
                </View>
              ) : (
                <View style={[st.formFoot, { borderTopColor: theme.colors.rule }]}>
                  <Text variant="meta" tone="fgMute" style={{ flex: 1, minWidth: 220 }}>
                    {t('kontakt.form.privacyNote')}
                  </Text>
                  <Button variant="primary" size="lg" trailingArrow onPress={onSubmit} disabled={submitting}>
                    {submitting ? t('kontakt.form.submitting') : t('kontakt.form.submit')}
                  </Button>
                </View>
              )}
            </View>

            <View style={st.infoCol}>
              {/* Direct mail — dark */}
              <View
                style={[
                  st.darkCard,
                  { backgroundColor: theme.colors.fg, borderRadius: 12, borderColor: 'rgba(137,93,255,0.4)' },
                  Platform.OS === 'web'
                    ? ({ boxShadow: `0 0 60px -20px ${palette.wine}55, 0 16px 40px -16px #15082E80` } as any)
                    : null,
                ]}
              >
                <Text variant="meta" style={{ color: palette.emberSoft }}>{t('kontakt.info.directMail.label')}</Text>
                <Text
                  style={{
                    fontFamily: theme.fontFamily.display,
                    fontSize: 28,
                    lineHeight: 32,
                    color: palette.paper,
                    marginTop: 12,
                  }}
                >
                  {t('kontakt.info.directMail.title')}
                </Text>
                <Text
                  italic
                  numberOfLines={1}
                  adjustsFontSizeToFit
                  style={{
                    fontFamily: theme.fontFamily.displayItalic,
                    fontStyle: 'italic',
                    fontSize: fluidFontSize(22, 6.5, 32) as any,
                    lineHeight: fluidLineHeight(1.1, 36) as any,
                    color: palette.emberSoft,
                    marginTop: 12,
                  }}
                >
                  {t('kontakt.info.directMail.email')}
                </Text>
                <Text style={{ color: 'rgba(245,240,255,0.78)', fontSize: 14, lineHeight: 22, marginTop: 16 }}>
                  {t('kontakt.info.directMail.body')}
                </Text>
              </View>

              {/* Response time */}
              <View
                style={[
                  st.infoCard,
                  { backgroundColor: theme.colors.bg, borderColor: theme.colors.rule, borderRadius: 12 },
                ]}
              >
                <Text variant="meta" style={{ color: theme.colors.accent }}>{t('kontakt.info.responseTime.label')}</Text>
                <Text
                  style={{
                    fontFamily: theme.fontFamily.display,
                    fontSize: 28,
                    lineHeight: 32,
                    color: theme.colors.fg,
                    marginTop: 8,
                  }}
                >
                  {t('kontakt.info.responseTime.titlePart1')}
                  <Text italic style={{ fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic', color: theme.colors.accent, fontSize: 28 }}>
                    {t('kontakt.info.responseTime.titleItalic')}
                  </Text>
                  {t('kontakt.info.responseTime.titlePart2')}
                </Text>
                <Text variant="body" tone="fgSoft" style={{ marginTop: 8 }}>
                  {t('kontakt.info.responseTime.bodyPart1')}
                  <Text style={{ fontWeight: '500', color: theme.colors.fg }}>{t('kontakt.info.responseTime.bodyHours')}</Text>
                  {t('kontakt.info.responseTime.bodyPart2')}
                </Text>
              </View>

              {/* Languages */}
              <View
                style={[
                  st.infoCard,
                  { backgroundColor: theme.colors.bg, borderColor: theme.colors.rule, borderRadius: 12 },
                ]}
              >
                <Text variant="meta" style={{ color: theme.colors.accent }}>{t('kontakt.info.languages.label')}</Text>
                <Text
                  style={{
                    fontFamily: theme.fontFamily.display,
                    fontSize: 28,
                    lineHeight: 32,
                    color: theme.colors.fg,
                    marginTop: 8,
                  }}
                >
                  {t('kontakt.info.languages.titlePart1')}
                  <Text italic style={{ fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic', color: theme.colors.accent, fontSize: 28 }}>
                    {t('kontakt.info.languages.titleItalic')}
                  </Text>
                </Text>
                <Text variant="body" tone="fgSoft" style={{ marginTop: 8 }}>
                  {t('kontakt.info.languages.body')}
                </Text>
              </View>
            </View>
          </View>
        </Container>
      </View>

      {/* 02 Standorte */}
      <View style={[st.standorteBg, { backgroundColor: theme.colors.bgSoft }]}>
        <Container>
          <SectionHead
            idx={t('kontakt.standorte.idx')}
            title={t('kontakt.standorte.title')}
            italic={t('kontakt.standorte.italic')}
            sub={t('kontakt.standorte.sub')}
          />
          <View style={st.studioRow}>
            {studios.map((s) => (
              <StudioCard key={s.name} s={s} />
            ))}
          </View>
        </Container>
      </View>

      {/* 03 Social */}
      <View style={{ paddingVertical: 96 }}>
        <Container>
          <SectionHead
            idx={t('kontakt.social.idx')}
            title={t('kontakt.social.title')}
            italic={t('kontakt.social.italic')}
            sub={t('kontakt.social.sub')}
          />
          <View style={st.socialGrid}>
            {socials.map((s) => (
              <SocialCard key={s.name} s={s} />
            ))}
          </View>
        </Container>
      </View>

      {/* 04+05 Hours + FAQ */}
      <View style={[st.hoursBg, { backgroundColor: theme.colors.bgSoft }]}>
        <Container>
          <View style={st.hoursGrid}>
            <View style={st.hoursCol}>
              <Text variant="meta" tone="fgMute">{t('kontakt.hours.eyebrow')}</Text>
              <Text
                style={{
                  fontFamily: theme.fontFamily.display,
                  fontSize: 40,
                  lineHeight: 44,
                  color: theme.colors.fg,
                  marginTop: 14,
                }}
              >
                {t('kontakt.hours.titlePart1')}
                <Text italic style={{ fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic', color: theme.colors.accent, fontSize: 40 }}>
                  {t('kontakt.hours.titleItalic')}
                </Text>
              </Text>
              <View style={[st.hoursList, { borderTopColor: theme.colors.rule }]}>
                {hoursRows.map((h, i) => {
                  const isClosed = i === hoursRows.length - 1;
                  return (
                    <View
                      key={h.day}
                      style={[
                        st.hoursRow,
                        { borderBottomColor: theme.colors.rule, borderTopColor: theme.colors.rule, borderTopWidth: i === 0 ? 0 : 1 },
                      ]}
                    >
                      <Text variant="meta" style={{ color: theme.colors.accent, flexBasis: 60 }}>{h.day}</Text>
                      <Text
                        style={{
                          color: isClosed ? theme.colors.fgMute : theme.colors.fg,
                          fontStyle: isClosed ? 'italic' : 'normal',
                          flex: 1,
                        }}
                      >
                        {h.text}
                      </Text>
                    </View>
                  );
                })}
              </View>
              <Text variant="meta" tone="fgMute" style={{ marginTop: 24 }}>
                {t('kontakt.hours.footnote')}
              </Text>
            </View>

            <View style={st.hoursCol}>
              <Text variant="meta" tone="fgMute">{t('kontakt.faq.eyebrow')}</Text>
              <Text
                style={{
                  fontFamily: theme.fontFamily.display,
                  fontSize: 40,
                  lineHeight: 44,
                  color: theme.colors.fg,
                  marginTop: 14,
                  marginBottom: 24,
                }}
              >
                {t('kontakt.faq.titlePart1')}
                <Text italic style={{ fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic', color: theme.colors.accent, fontSize: 40 }}>
                  {t('kontakt.faq.titleItalic')}
                </Text>
              </Text>
              <FaqAccordion items={faqItems} />
            </View>
          </View>
        </Container>
      </View>
    </View>
  );
}

function TopicChip({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  const theme = useTheme();
  const { hovered, hoverProps } = useHover();
  const bg = active || hovered ? theme.colors.fg : 'transparent';
  const color = active || hovered ? palette.paper : theme.colors.fgSoft;
  return (
    <Pressable
      {...(hoverProps as any)}
      accessibilityRole="button"
      onPress={onPress}
      style={[
        st.chip,
        { backgroundColor: bg, borderColor: active ? theme.colors.fg : theme.colors.rule, borderRadius: 999 },
        webTransition('background-color, color, border-color'),
      ]}
    >
      <Text variant="meta" style={{ color }}>{label}</Text>
    </Pressable>
  );
}

function Field({
  label,
  value,
  onChangeText,
  multiline,
  full,
  kind,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  multiline?: boolean;
  full?: boolean;
  kind?: 'email';
}) {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  return (
    <View style={[full ? st.fieldFull : st.field]}>
      <Text variant="eyebrow" tone="fgMute" style={{ marginBottom: 6 }}>{label}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        multiline={multiline}
        numberOfLines={multiline ? 5 : 1}
        keyboardType={kind === 'email' ? 'email-address' : 'default'}
        autoCapitalize="none"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={[
          st.input,
          multiline && { minHeight: 120, textAlignVertical: 'top' as const, paddingTop: 12 },
          {
            color: theme.colors.fg,
            backgroundColor: theme.colors.bg,
            borderColor: focused ? theme.colors.accent : theme.colors.rule,
            borderRadius: 6,
            fontFamily: theme.fontFamily.body as string,
          },
          focused && Platform.OS === 'web' ? ({ boxShadow: `0 0 0 3px ${palette.wine}22` } as any) : null,
          webTransition('border-color, box-shadow'),
        ]}
      />
    </View>
  );
}

function StudioCard({ s }: { s: StudioDecorated }) {
  const theme = useTheme();
  const { hovered, hoverProps } = useHover();
  const color = s.color === 'wine' ? palette.wine : palette.verdeSlate;
  const altColor = s.color === 'wine' ? palette.wineDeep : '#1c2a4a';
  return (
    <Pressable
      {...(hoverProps as any)}
      style={[
        st.studio,
        {
          borderRadius: 12,
          overflow: 'hidden',
          borderColor: hovered ? theme.colors.accent : theme.colors.rule,
        },
        webTransition(),
        hovered ? { transform: [{ translateY: -3 }] } : null,
      ]}
    >
      <View style={st.studioHero}>
        <View
          style={[
            st.studioStripes,
            { backgroundColor: color },
            Platform.OS === 'web'
              ? ({
                  backgroundImage: `repeating-linear-gradient(135deg, ${color} 0, ${color} 16px, ${altColor} 16px, ${altColor} 32px)`,
                } as any)
              : null,
          ]}
        />
        <View style={st.studioHeroOverlay} />
        <View style={st.studioHeroContent}>
          <Text variant="meta" style={{ color: 'rgba(245,240,255,0.85)' }}>{s.label}</Text>
          <Text
            italic
            style={{
              fontFamily: 'InstrumentSerif_400Regular_Italic',
              fontStyle: 'italic',
              fontSize: 64,
              lineHeight: 64,
              color: palette.paper,
            }}
          >
            {s.initial}
          </Text>
        </View>
      </View>
      <View style={[st.studioBody, { backgroundColor: theme.colors.bg, borderColor: theme.colors.rule }]}>
        <Pill variant={s.color}>{s.pill}</Pill>
        <Text
          style={{
            fontFamily: theme.fontFamily.display,
            fontSize: 32,
            lineHeight: 36,
            color: theme.colors.fg,
            marginTop: 12,
          }}
        >
          {s.name.replace(s.nameItalic, '').trim()}{' '}
          <Text italic style={{ fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic', color: theme.colors.accent, fontSize: 32 }}>
            {s.nameItalic}
          </Text>
        </Text>
        <Text variant="meta" tone="fgSoft" style={{ marginTop: 8 }}>{s.role}</Text>
        <Text
          italic
          style={{
            fontFamily: theme.fontFamily.displayItalic,
            fontStyle: 'italic',
            fontSize: 22,
            color: theme.colors.fg,
            marginTop: 16,
          }}
        >
          {s.address}
        </Text>
        <Text variant="meta" tone="fgMute" style={{ marginTop: 4 }}>{s.city}</Text>
        <Text variant="body" tone="fgSoft" style={{ marginTop: 12, maxWidth: 420 }}>{s.body}</Text>
      </View>
    </Pressable>
  );
}

function SocialCard({ s }: { s: SocialDecorated }) {
  const theme = useTheme();
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...(hoverProps as any)}
      style={[
        st.social,
        {
          backgroundColor: hovered ? theme.colors.fg : theme.colors.bgSoft,
          borderColor: hovered ? theme.colors.fg : theme.colors.rule,
          borderRadius: 12,
        },
        webTransition(),
        hovered ? { transform: [{ translateY: -3 }] } : null,
      ]}
    >
      <View style={[st.socialIcon, { backgroundColor: hovered ? palette.ember : s.bg, borderRadius: 16 }]}>
        <Text
          italic
          style={{
            fontFamily: 'InstrumentSerif_400Regular_Italic',
            fontStyle: 'italic',
            fontSize: 28,
            color: s.dark && !hovered ? palette.ink : palette.paper,
          }}
        >
          {s.initial}
        </Text>
      </View>
      <Text
        style={{
          fontFamily: theme.fontFamily.display,
          fontSize: 28,
          color: hovered ? palette.paper : theme.colors.fg,
          marginTop: 16,
        }}
      >
        {s.name}
      </Text>
      <Text variant="meta" style={{ color: hovered ? 'rgba(245,240,255,0.6)' : theme.colors.fgMute, marginTop: 4 }}>
        {s.handle}
      </Text>
      <Text
        style={{
          fontSize: 14,
          lineHeight: 22,
          color: hovered ? 'rgba(245,240,255,0.78)' : theme.colors.fgSoft,
          marginTop: 12,
        }}
      >
        {s.body}
      </Text>
      <Text
        variant="meta"
        style={{ color: hovered ? palette.emberSoft : theme.colors.accent, marginTop: 'auto', paddingTop: 16 }}
      >
        {s.cta} →
      </Text>
    </Pressable>
  );
}

const st = StyleSheet.create({
  eyebrowRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 32 },
  dot: { width: 6, height: 6, borderRadius: 3 },
  pulse: {
    marginTop: 32,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 22,
    borderWidth: 1,
  },
  pulseDot: { width: 8, height: 8, borderRadius: 4 },

  formGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' },
  formCard: { flex: 1.3, minWidth: 320, padding: 40, borderWidth: 1 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 24 },
  chip: { paddingVertical: 8, paddingHorizontal: 14, borderWidth: 1 },
  fieldGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginTop: 24 },
  field: { flex: 1, minWidth: 200 },
  fieldFull: { flexBasis: '100%' },
  input: { borderWidth: 1, paddingHorizontal: 14, paddingVertical: 10, fontSize: 15, ...(Platform.OS === 'web' ? { outlineWidth: 0 } : {}) } as any,
  formFoot: {
    marginTop: 32,
    paddingTop: 24,
    borderTopWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
    justifyContent: 'space-between',
  },
  thanks: { marginTop: 24, padding: 24 },

  infoCol: { flex: 1, minWidth: 320, gap: 16 },
  darkCard: { padding: 28, borderWidth: 1 },
  infoCard: { padding: 28, borderWidth: 1 },

  standorteBg: { paddingVertical: 96 },
  studioRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 24 },
  studio: { flex: 1, minWidth: 320, borderWidth: 1 },
  studioHero: { aspectRatio: 16 / 9, position: 'relative' },
  studioStripes: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 },
  studioHeroOverlay: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundColor: 'rgba(12,8,20,0.25)' },
  studioHeroContent: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, padding: 22, justifyContent: 'space-between' },
  studioBody: { padding: 28, borderTopWidth: 0 },

  socialGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
  social: { flex: 1, minWidth: 220, padding: 28, borderWidth: 1 },
  socialIcon: { width: 48, height: 48, alignItems: 'center', justifyContent: 'center' },

  hoursBg: { paddingVertical: 96 },
  hoursGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 56 },
  hoursCol: { flex: 1, minWidth: 320 },
  hoursList: { borderTopWidth: 1, marginTop: 16 },
  hoursRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, gap: 18, borderBottomWidth: 1, flexWrap: 'wrap' },
});
